import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { NewOrderButton } from "@/components/NewOrderButton";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import { useRealtime } from "@/lib/useRealtime";
import {
  listOrdersWithFallback,
  getOrderFilterOptions,
  cancelOrder,
  restoreOrder,
  markOrderDelivered,
  unmarkOrderDelivered,
} from "@/lib/orders";
import type { OrderHistoryRow } from "@/lib/orders";
import { OrdersHistorySection } from "@/pages/bestellungen/OrdersHistorySection";
import { OrderDetailDialog } from "@/pages/bestellungen/OrderDetailDialog";
import { InvoiceForOrderDialog } from "@/pages/bestellungen/InvoiceForOrderDialog";
import { OrderStornoDialog, type OrderStornoDialogMode } from "@/pages/bestellungen/OrderStornoDialog";

const ORDERS_PAGE_SIZE = 200;
import { OrderFilterSelect } from "@/pages/bestellungen/OrderFilterSelect";
import { useOrderWizard } from "@/pages/bestellungen/useOrderWizard";
import {
  ORDER_HISTORY_SORT_OPTIONS,
  sortOrderHistoryRows,
  type OrderHistorySortKey,
} from "@/pages/bestellungen/orderHistoryDisplay";
import {
  getInvoiceMailOrderHints,
  type InvoiceMailOrderHint,
} from "@/lib/invoiceMail";

export function BestellungenIndexPage() {
  const { user } = useCurrentUser();
  const canMarkDelivered = userIsBuero(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    openDraftById,
    openEdit,
    beginReorder,
    subscribeNotice,
    subscribeOrdersChanged,
  } = useOrderWizard();

  const [rows, setRows] = useState<Awaited<ReturnType<typeof listOrdersWithFallback>>["rows"]>([]);
  const [altMode, setAltMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestSeqRef = useRef(0);
  const nextOffsetRef = useRef(0);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [fuzzy, setFuzzy] = useState(true);
  const [filterSupplier, setFilterSupplier] = useState("");
  const [filterBesteller, setFilterBesteller] = useState("");
  const [filterContact, setFilterContact] = useState("");
  const [openOnly, setOpenOnly] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    suppliers: [] as string[],
    bestellers: [] as string[],
    contacts: [] as string[],
  });
  const [sortKey, setSortKey] = useState<OrderHistorySortKey>("newest");

  const [detailOrder, setDetailOrder] = useState<OrderHistoryRow | null>(null);
  const [invoiceHints, setInvoiceHints] = useState<Record<number, InvoiceMailOrderHint>>({});
  const [invoiceDialogOrder, setInvoiceDialogOrder] = useState<OrderHistoryRow | null>(null);
  const [stornoDialog, setStornoDialog] = useState<{
    open: boolean;
    mode: OrderStornoDialogMode;
    row: OrderHistoryRow | null;
    loading: boolean;
  }>({ open: false, mode: "cancel", row: null, loading: false });

  useEffect(() => {
    const h = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(h);
  }, [search]);

  useEffect(() => {
    if (!detailOrder) return;
    const updated = rows.find((r) => r.id === detailOrder.id);
    if (updated) setDetailOrder(updated);
  }, [rows, detailOrder?.id]);

  const historyQuery = useMemo(
    () => ({
      q: debouncedSearch || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      supplierName: filterSupplier || undefined,
      createdBy: filterBesteller || undefined,
      contactName: filterContact || undefined,
      typo: fuzzy,
      openOnly,
    }),
    [debouncedSearch, dateFrom, dateTo, filterSupplier, filterBesteller, filterContact, fuzzy, openOnly],
  );

  const reloadHistory = useCallback(async (options?: { silent?: boolean }) => {
    const requestId = ++requestSeqRef.current;
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const { rows: list, altMode: alt } = await listOrdersWithFallback({
        ...historyQuery,
        limit: ORDERS_PAGE_SIZE,
        offset: 0,
      });
      if (requestId !== requestSeqRef.current) return;
      setAltMode(alt);
      const keepOlder = options?.silent && nextOffsetRef.current > list.length;
      if (keepOlder) {
        setRows((prev) => {
          const freshIds = new Set(list.map((row) => row.id));
          return [...list, ...prev.filter((row) => !freshIds.has(row.id))];
        });
      } else {
        setRows(list);
        setHasMore(list.length >= ORDERS_PAGE_SIZE);
        nextOffsetRef.current = list.length;
      }
    } catch (err) {
      if (requestId !== requestSeqRef.current) return;
      setError(err instanceof ApiError ? err.message : "Liste konnte nicht geladen werden.");
      if (!options?.silent) {
        setRows([]);
        setHasMore(false);
        nextOffsetRef.current = 0;
      }
    } finally {
      if (requestId === requestSeqRef.current) setLoading(false);
    }
  }, [historyQuery]);

  const loadMoreHistory = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    const requestId = requestSeqRef.current;
    const offset = nextOffsetRef.current;
    setLoadingMore(true);
    try {
      const { rows: list } = await listOrdersWithFallback({
        ...historyQuery,
        limit: ORDERS_PAGE_SIZE,
        offset,
      });
      if (requestId !== requestSeqRef.current) return;
      setRows((prev) => {
        const seen = new Set(prev.map((row) => row.id));
        const extra = list.filter((row) => !seen.has(row.id));
        return extra.length ? [...prev, ...extra] : prev;
      });
      setHasMore(list.length >= ORDERS_PAGE_SIZE);
      nextOffsetRef.current = offset + list.length;
    } catch (err) {
      console.warn("[Bestellungen] Weitere Bestellungen konnten nicht geladen werden:", err);
    } finally {
      if (requestId === requestSeqRef.current) setLoadingMore(false);
    }
  }, [historyQuery, hasMore, loading, loadingMore]);

  const reloadHistoryRef = useRef(reloadHistory);
  reloadHistoryRef.current = reloadHistory;

  const scheduleReloadHistory = useCallback(() => {
    void reloadHistoryRef.current({ silent: true });
  }, []);

  useEffect(() => {
    void reloadHistory();
  }, [reloadHistory]);

  useEffect(() => {
    if (!canMarkDelivered || !rows.length) {
      setInvoiceHints({});
      return;
    }
    const ids = rows.map((r) => r.id);
    void getInvoiceMailOrderHints(ids)
      .then(setInvoiceHints)
      .catch(() => setInvoiceHints({}));
  }, [rows, canMarkDelivered]);

  useEffect(() => {
    void getOrderFilterOptions({
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      supplierName: filterSupplier || undefined,
      createdBy: filterBesteller || undefined,
      contactName: filterContact || undefined,
      openOnly,
    }).then(setFilterOptions);
  }, [dateFrom, dateTo, filterSupplier, filterBesteller, filterContact, openOnly]);

  useEffect(
    () =>
      subscribeNotice((msg) => {
        if (msg) toast.success(msg);
      }),
    [subscribeNotice],
  );

  useEffect(
    () => subscribeOrdersChanged(scheduleReloadHistory),
    [subscribeOrdersChanged, scheduleReloadHistory],
  );

  useRealtime({
    scopes: ["orders:list"],
    domains: ["orders"],
    events: ["orders_changed"],
    onChange: scheduleReloadHistory,
    fallbackIntervalMs: 180_000,
  });

  useEffect(() => {
    const draftId = searchParams.get("id") || searchParams.get("draft");
    if (!draftId) return;
    const n = Number(draftId);
    if (!Number.isFinite(n) || n <= 0) return;
    void openDraftById(n);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, openDraftById]);

  const hasActiveFilters = !!(
    filterSupplier || filterBesteller || filterContact || dateFrom || dateTo || openOnly
  );

  const activeFilterLabels = useMemo(() => {
    const parts: string[] = [];
    if (filterSupplier) parts.push(`Lieferant: ${filterSupplier}`);
    if (filterContact) parts.push(`Ansprechpartner: ${filterContact}`);
    if (filterBesteller) parts.push(`Besteller: ${filterBesteller}`);
    if (dateFrom) parts.push(`ab ${dateFrom}`);
    if (dateTo) parts.push(`bis ${dateTo}`);
    if (openOnly) parts.push("Nur offene");
    return parts;
  }, [filterSupplier, filterContact, filterBesteller, dateFrom, dateTo, openOnly]);

  const sortedRows = useMemo(() => sortOrderHistoryRows(rows, sortKey), [rows, sortKey]);

  function resetFilters() {
    setFilterSupplier("");
    setFilterBesteller("");
    setFilterContact("");
    setDateFrom("");
    setDateTo("");
    setOpenOnly(false);
  }

  const openStornoDialog = useCallback((mode: OrderStornoDialogMode, row: OrderHistoryRow) => {
    setStornoDialog({ open: true, mode, row, loading: false });
  }, []);

  const closeStornoDialog = useCallback(() => {
    setStornoDialog((prev) => ({ ...prev, open: false, loading: false }));
  }, []);

  const handleMarkDelivered = useCallback(
    async (row: OrderHistoryRow) => {
      try {
        const result = await markOrderDelivered(row.id);
        toast.success("Bestellung als geliefert markiert.");
        if (result.geliefert_at) {
          setRows((prev) =>
            openOnly
              ? prev.filter((r) => r.id !== row.id)
              : prev.map((r) =>
                  r.id === row.id ? { ...r, geliefert_at: result.geliefert_at } : r,
                ),
          );
        }
        await reloadHistory({ silent: true });
      } catch (err) {
        toast.error(
          err instanceof ApiError ? err.message : "Lieferung konnte nicht markiert werden.",
        );
        throw err;
      }
    },
    [reloadHistory, openOnly],
  );

  const handleUnmarkDelivered = useCallback(
    async (row: OrderHistoryRow) => {
      try {
        await unmarkOrderDelivered(row.id);
        toast.success("Liefer-Markierung wurde zurückgenommen.");
        setRows((prev) =>
          prev.map((r) => (r.id === row.id ? { ...r, geliefert_at: null } : r)),
        );
        await reloadHistory({ silent: true });
      } catch (err) {
        toast.error(
          err instanceof ApiError ? err.message : "Rücknahme der Lieferung fehlgeschlagen.",
        );
        throw err;
      }
    },
    [reloadHistory],
  );

  const handleStornoConfirm = useCallback(async () => {
    const { mode, row } = stornoDialog;
    if (!row) return;
    setStornoDialog((prev) => ({ ...prev, loading: true }));
    try {
      if (mode === "cancel") {
        await cancelOrder(row.id);
        toast.success("Bestellung wurde storniert.");
      } else {
        await restoreOrder(row.id);
        toast.success("Stornierung wurde aufgehoben.");
      }
      closeStornoDialog();
      await reloadHistory({ silent: true });
    } catch (err) {
      setStornoDialog((prev) => ({ ...prev, loading: false }));
      toast.error(
        err instanceof ApiError
          ? err.message
          : mode === "cancel"
            ? "Stornierung fehlgeschlagen."
            : "Aufheben der Stornierung fehlgeschlagen.",
      );
    }
  }, [stornoDialog, closeStornoDialog, reloadHistory]);

  return (
    <AppShell
      title="Bestellungen"
      subtitle="Bestellungen anlegen, versenden und Historie durchsuchen."
      actions={<NewOrderButton />}
    >
      {error ? (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      ) : null}

      <section className="rounded-ios border border-separator bg-card p-3 shadow-ios">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[12rem] flex-1 space-y-1">
            <label className="eh-label text-xs" htmlFor="orders-search">
              Suche
            </label>
            <input
              id="orders-search"
              type="search"
              className="eh-input w-full"
              placeholder="Lieferant, Betreff, Artikel, Bestellnummer …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-1 text-xs text-label-secondary">
            <span>von</span>
            <input
              type="date"
              className="eh-input !py-1.5 text-sm"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-1 text-xs text-label-secondary">
            <span>bis</span>
            <input
              type="date"
              className="eh-input !py-1.5 text-sm"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-1.5 pb-2 text-xs text-label">
            <input type="checkbox" checked={fuzzy} onChange={(e) => setFuzzy(e.target.checked)} />
            fehlertolerant
          </label>
          <label
            className="flex items-center gap-1.5 pb-2 text-xs text-label"
            title="Bereits gelieferte und stornierte Bestellungen ausblenden"
          >
            <input
              type="checkbox"
              checked={openOnly}
              onChange={(e) => setOpenOnly(e.target.checked)}
            />
            Nur offene
          </label>
          <div className="min-w-[12rem] space-y-1">
            <label className="eh-label text-xs" htmlFor="orders-sort">
              Sortierung
            </label>
            <select
              id="orders-sort"
              className="eh-input w-full"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as OrderHistorySortKey)}
            >
              {ORDER_HISTORY_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <OrderFilterSelect
            label="Lieferant"
            value={filterSupplier}
            options={filterOptions.suppliers}
            onChange={setFilterSupplier}
            placeholder="Alle Lieferanten"
          />
          <OrderFilterSelect
            label="Ansprechpartner"
            value={filterContact}
            options={filterOptions.contacts}
            onChange={setFilterContact}
            placeholder="Alle Ansprechpartner"
          />
          <OrderFilterSelect
            label="Besteller"
            value={filterBesteller}
            options={filterOptions.bestellers}
            onChange={setFilterBesteller}
            placeholder="Alle Besteller"
          />
        </div>

        {hasActiveFilters ? (
          <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-separator pt-2">
            <span className="text-xs text-label-secondary">Aktive Filter:</span>
            {activeFilterLabels.map((label) => (
              <span key={label} className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs text-label">
                {label}
              </span>
            ))}
            <Button type="button" size="sm" variant="ghost" onClick={resetFilters}>
              Alle zurücksetzen
            </Button>
          </div>
        ) : null}
      </section>

      {loading ? (
        <p className="mt-4 flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lade Bestellungen …
        </p>
      ) : null}
      {!loading ? (
        <section className="mt-4" aria-label="Suchergebnisse">
          <OrdersHistorySection
            rows={sortedRows}
            altMode={altMode}
            emptyMessage={
              openOnly ? "Keine offenen Bestellungen gefunden." : undefined
            }
            invoiceHints={invoiceHints}
            onOpen={setDetailOrder}
            onViewInvoice={canMarkDelivered ? setInvoiceDialogOrder : undefined}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMoreHistory}
          />
        </section>
      ) : null}

      <OrderDetailDialog
        open={detailOrder != null}
        order={detailOrder}
        canMarkDelivered={canMarkDelivered}
        invoiceHint={detailOrder ? invoiceHints[detailOrder.id] : undefined}
        onViewInvoice={
          canMarkDelivered && detailOrder
            ? () => {
                setInvoiceDialogOrder(detailOrder);
              }
            : undefined
        }
        onClose={() => setDetailOrder(null)}
        onOrderChange={(next) => {
          setDetailOrder(next);
          setRows((prev) => prev.map((row) => (row.id === next.id ? next : row)));
        }}
        onReorderAll={(row) => {
          setDetailOrder(null);
          beginReorder(row, null);
        }}
        onReorderOne={(row, idx) => {
          setDetailOrder(null);
          beginReorder(row, [idx]);
        }}
        onEdit={(row) => {
          setDetailOrder(null);
          void openEdit(row);
        }}
        onRequestCancel={(row) => {
          setDetailOrder(null);
          openStornoDialog("cancel", row);
        }}
        onRequestRestore={(row) => {
          setDetailOrder(null);
          openStornoDialog("restore", row);
        }}
        onMarkDelivered={handleMarkDelivered}
        onUnmarkDelivered={handleUnmarkDelivered}
      />
      <InvoiceForOrderDialog
        open={invoiceDialogOrder != null}
        orderId={invoiceDialogOrder?.id ?? null}
        bestellnummern={invoiceDialogOrder?.bestellnummern ?? null}
        onClose={() => setInvoiceDialogOrder(null)}
      />
      <OrderStornoDialog
        open={stornoDialog.open}
        mode={stornoDialog.mode}
        row={stornoDialog.row}
        loading={stornoDialog.loading}
        onClose={closeStornoDialog}
        onConfirm={() => void handleStornoConfirm()}
      />
    </AppShell>
  );
}
